import './DtcCommerceMmm.scss';
import { useState } from 'react';
import { AgentSessionManager } from '@Components/AgentChat';
import { FloatingPanel } from '@Components/FloatingPanel';
import { Page } from '@Components/Page/Page';
import { AttributionTooltip, useAttributionTooltip } from './components/AttributionTooltip';
import { ChannelDrawer } from './components/ChannelDrawer';
import { ChannelPerformance } from './components/ChannelPerformance';
import { DtcCommerceMmmFilters } from './components/DtcCommerceMmmFilters';
import { DtcCommerceMmmRunPicker } from './components/DtcCommerceMmmRunPicker';
import { DtcCommerceMmmSkeleton } from './components/DtcCommerceMmmSkeleton';
import { GlossaryModal } from './components/GlossaryModal';
import { GlossaryButton } from './components/GlossaryButton';
import { ModelDiagnosticsModal } from './components/ModelDiagnosticsModal';
import { ModelFitButton } from './components/ModelFitButton';
import { MarketPerformance } from './components/MarketPerformance';
import { ModelHighlights } from './components/ModelHighlights';
import {
  DtcCommerceMmmDataProvider,
  useDtcCommerceMmmData
} from './context/DtcCommerceMmmDataContext';
import { DEFAULT_HIERARCHY, GEMINI_LOGO_URL } from './dtc-commerce-mmm-constants';
import { MmmDashboardConfig } from './dtc-commerce-mmm-variants';
import { DashboardData, HierarchyLevel } from './dtc-commerce-mmm-types';
import { useFilteredDashboardData } from './hooks/useFilteredDashboardData';
import { Agent, useAgentEndpoint } from '../../../utils/agent-endpoints';
import { useCompanyInfo } from '../../../utils/hooks/useCompanyInfo';

interface DtcCommerceMmmBodyProps {
  hierarchyLevel: HierarchyLevel;
  selectedChannel: string | null;
  focusedFeatureName: string | null;
  onChannelClick: (groupKey: string, focusedFeatureName?: string) => void;
  onChannelClose: () => void;
  showDiagnostics: boolean;
  onCloseDiagnostics: () => void;
}

const DtcCommerceMmmBody = ({
  hierarchyLevel,
  selectedChannel,
  focusedFeatureName,
  onChannelClick,
  onChannelClose,
  showDiagnostics,
  onCloseDiagnostics
}: DtcCommerceMmmBodyProps) => {
  const { data: rawData, loading, dateRange, attributionType, config } = useDtcCommerceMmmData();
  const data: DashboardData | null = useFilteredDashboardData(rawData, dateRange);
  const { show: showAttributionTooltip, dismiss: dismissAttributionTooltip } =
    useAttributionTooltip(attributionType);

  if (loading) return <DtcCommerceMmmSkeleton />;

  // Load/discovery failures surface via the shared error modal (useSetError); this is the
  // empty state for a client with no runs.
  if (!data) {
    return (
      <div className='dtc-commerce-mmm-error'>
        <div className='dtc-commerce-mmm-status-title'>No {config.title} data</div>
        <div className='dtc-commerce-mmm-status-detail'>
          No model runs available for this client.
        </div>
      </div>
    );
  }

  return (
    <>
      <ModelHighlights
        data={data}
        hierarchyLevel={hierarchyLevel}
        onChannelClick={onChannelClick}
      />
      <ChannelPerformance
        data={data}
        hierarchyLevel={hierarchyLevel}
        onChannelClick={onChannelClick}
      />
      {config.showMarkets && <MarketPerformance data={data} />}
      <ChannelDrawer
        data={data}
        selectedGroupKey={selectedChannel}
        clickedLevel={hierarchyLevel}
        focusedFeatureName={focusedFeatureName}
        onClose={onChannelClose}
      />
      <ModelDiagnosticsModal show={showDiagnostics} onHide={onCloseDiagnostics} data={data} />
      <AttributionTooltip show={showAttributionTooltip} onDismiss={dismissAttributionTooltip} />
    </>
  );
};

export const DtcCommerceMmm = ({ config }: { config: MmmDashboardConfig }) => {
  const [hierarchyLevel, setHierarchyLevel] = useState<HierarchyLevel>(DEFAULT_HIERARCHY);
  const [selectedChannel, setSelectedChannel] = useState<string | null>(null);
  const [focusedFeatureName, setFocusedFeatureName] = useState<string | null>(null);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [showGlossary, setShowGlossary] = useState(false);
  // Agent panel starts closed; toggled from the sparkle button in the subheader.
  const [agentOpen, setAgentOpen] = useState(false);

  const agentEndpoint = useAgentEndpoint(Agent.MMM);
  const { cid: company } = useCompanyInfo();

  const handleChannelClick = (groupKey: string, featureName?: string) => {
    setSelectedChannel(groupKey);
    setFocusedFeatureName(featureName ?? null);
  };

  const handleChannelClose = () => {
    setSelectedChannel(null);
    setFocusedFeatureName(null);
  };

  return (
    <DtcCommerceMmmDataProvider config={config}>
      <Page
        title={config.title}
        pageType={config.pageType}
        actions={<DtcCommerceMmmRunPicker />}
        subHeader={
          <div className='dtc-commerce-mmm-subheader'>
            <DtcCommerceMmmFilters
              hierarchyLevel={hierarchyLevel}
              onHierarchyChange={setHierarchyLevel}
              agentOpen={agentOpen}
              onToggleAgent={() => setAgentOpen(open => !open)}
            />
            <div className='dtc-commerce-mmm-subheader-actions'>
              <GlossaryButton onClick={() => setShowGlossary(true)} />
              <ModelFitButton onClick={() => setShowDiagnostics(true)} />
            </div>
          </div>
        }
      >
        <div className='dtc-commerce-mmm'>
          <DtcCommerceMmmBody
            hierarchyLevel={hierarchyLevel}
            selectedChannel={selectedChannel}
            focusedFeatureName={focusedFeatureName}
            onChannelClick={handleChannelClick}
            onChannelClose={handleChannelClose}
            showDiagnostics={showDiagnostics}
            onCloseDiagnostics={() => setShowDiagnostics(false)}
          />
        </div>
        <GlossaryModal show={showGlossary} onHide={() => setShowGlossary(false)} />
        <DtcCommerceMmmAgentPanel
          agentEndpoint={agentEndpoint}
          company={company}
          agentOpen={agentOpen}
          onHideAgent={() => setAgentOpen(false)}
        />
      </Page>
    </DtcCommerceMmmDataProvider>
  );
};

interface DtcCommerceMmmAgentPanelProps {
  agentEndpoint: string;
  company?: string;
  agentOpen: boolean;
  onHideAgent: () => void;
}

// Rendered inside DtcCommerceMmmDataProvider so it can read the run picker's selection via useDtcCommerceMmmData()
// (DtcCommerceMmm's own body renders the provider, so it can't call the hook itself). Open/closed state lives in
// DtcCommerceMmm because the subheader's sparkle toggle sits outside this component; the side-panel state and the
// selected KPI/timestamp forwarding stay here.
const DtcCommerceMmmAgentPanel = ({
  agentEndpoint,
  company,
  agentOpen,
  onHideAgent
}: DtcCommerceMmmAgentPanelProps) => {
  const [agentSidePanelOpen, setAgentSidePanelOpen] = useState(false);
  const { selectedKpi, selectedTimestamp, config } = useDtcCommerceMmmData();

  return (
    <FloatingPanel
      id='mmm-agent-panel'
      show={agentOpen}
      onHide={onHideAgent}
      showSidePanel={agentSidePanelOpen}
      setShowSidePanel={setAgentSidePanelOpen}
      leftActions={<AgentPanelTitle />}
      minWidth={342}
      maxWidth={1440}
      initialWidth={465}
      minHeight={396}
      initialHeight={745}
      initialPosition={{ xDist: 32, xDirection: 'right', yDist: 32, yDirection: 'bottom' }}
      body={
        <AgentSessionManager
          agentEndpoint={agentEndpoint}
          agent={Agent.MMM}
          company={company}
          kpi={selectedKpi ?? undefined}
          timestamp={selectedTimestamp ?? undefined}
          modelType={config.modelType}
          visible={agentOpen}
        />
      }
    />
  );
};

const AgentPanelTitle = () => (
  <div className='dtc-commerce-mmm-agent-panel-title'>
    <span className='dtc-commerce-mmm-agent-panel-name'>MMM Agent</span>
    <span className='dtc-commerce-mmm-agent-panel-powered-by'>
      Powered by
      <img src={GEMINI_LOGO_URL} alt='Google Gemini' />
    </span>
  </div>
);
